import React, { useState, useEffect } from 'react';
import { 
    StandardPageLayout, 
    Button, 
    FormInput, 
    PasswordInput,
    LoadingSpinner 
} from '../components/Common.jsx';
import { 
    User, 
    Lock, 
    Bell, 
    LogOut, 
    Camera,
    CheckCircle2,
    AlertCircle,
    X,
    ChevronLeft,
    ChevronRight,
    Search,
    Eye,
    EyeOff
} from 'lucide-react';
import { useAuth } from '../context/useAuth';
import { apiFetch } from '../api';
import { useNotification } from '../context/NotificationContext';
import { Avatar, Box, Typography, Divider, Stack, Paper, IconButton, InputAdornment } from '@mui/material';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator.jsx';
import { validatePassword } from '../utils/passwordValidation';
import { processProfileImage } from '../utils/imageProcess';

const ProfilePage = ({ onBack, forceTab }) => {
    const { user, logout, refreshUser, passwordPolicy } = useAuth();
    const { addNotification } = useNotification();
    const [activeTab, setActiveTab] = useState(forceTab || 'basic');
    const [isSidebarOpen, setSidebarOpen] = useState(!forceTab);
    const [loading, setLoading] = useState(false);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [originalProfileData, setOriginalProfileData] = useState(null);

    // Basic Details State
    const [profileData, setProfileData] = useState({
        name: user?.name || '',
        contact_number: user?.contact_number || '',
        email: user?.email || '',
        profile_pic: user?.profile_pic || null,
        company: user?.company || '',
        location: user?.location || '',
        state: user?.state || '',
        country: user?.country || ''
    });

    // Password State
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });

    const [isResetMode, setIsResetMode] = useState(true);
    const [resetLinkSent, setResetLinkSent] = useState(false);
    const [resetData, setResetData] = useState({
        email: user?.email || '',
        username: user?.name || ''
    });

    useEffect(() => {
        if (user) {
            const data = {
                name: user.name || '',
                contact_number: user.contact_number || '',
                email: user.email || '',
                profile_pic: user.profile_pic || null,
                company: user.company || '',
                location: user.location || '',
                state: user.state || '',
                country: user.country || ''
            };
            setProfileData(data);
            setOriginalProfileData(data);
        }
    }, [user]);

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await apiFetch('/users/me', null, {
                method: 'PUT',
                body: JSON.stringify({
                    name: profileData.name,
                    contact_number: profileData.contact_number,
                    profile_pic: profileData.profile_pic,
                    company: profileData.company,
                    location: profileData.location,
                    state: profileData.state,
                    country: profileData.country
                })
            });
            addNotification("Profile updated successfully", "success");
            setIsEditingProfile(false);
            if (refreshUser) await refreshUser();
        } catch (err) {
            addNotification(err.message || "Failed to update profile", "error");
        } finally {
            setLoading(false);
        }
    };

    // Validation for new password
    const passwordValidation = React.useMemo(() => validatePassword(passwordData.new_password, passwordPolicy), [passwordData.new_password, passwordPolicy]);


    const handlePasswordChange = async (e) => {
        e.preventDefault();
        
        // --- Mandatory Safety Net ---
        if (!passwordValidation.isValid) {
            addNotification(passwordValidation.errorMessage || 'Password does not meet requirements.', 'error');
            return;
        }

        if (passwordData.new_password !== passwordData.confirm_password) {
            addNotification("New passwords do not match", "error");
            return;
        }

        // console.log('[Password Validation Check] Button Disabled: NO — submit proceeding');

        setLoading(true);
        try {
            await apiFetch('/auth/change-password', null, {
                method: 'POST',
                body: JSON.stringify({
                    current_password: passwordData.current_password,
                    new_password: passwordData.new_password
                })
            });
            addNotification("Password changed successfully", "success");
            setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
            window.dispatchEvent(new CustomEvent('password-changed'));
        } catch (err) {
            addNotification(err.message || "Failed to change password", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleResetRequest = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await apiFetch('/auth/forgot-password', null, {
                method: 'POST',
                body: JSON.stringify({ email: resetData.email })
            });
            // ✅ Stay on the same screen — show a success confirmation
            setResetLinkSent(true);
        } catch (err) {
            addNotification(err.message || "Failed to send reset link. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        if (!isEditingProfile) return;
        const file = e.target.files[0];
        if (file) {
            try {
                const { dataUrl, error } = await processProfileImage(file);
                
                if (error) {
                    addNotification(error, "error");
                    return;
                }
                
                setProfileData(prev => ({ ...prev, profile_pic: dataUrl }));
                addNotification("Image processed and ready to save", "success");
            } catch (err) {
                console.error("Image processing error:", err);
                addNotification("Failed to process image. Please try another one.", "error");
            }
        }
    };

    const handleCancelEdit = () => {
        if (originalProfileData) {
            setProfileData(originalProfileData);
        }
        setIsEditingProfile(false);
    };

    const tabs = [
        { id: 'basic', label: 'Basic Details', icon: User },
        { id: 'password', label: 'Change Password', icon: Lock },
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'basic':
                return (
                    <div className="w-full max-w-4xl animate-fade-in space-y-6">
                        {/* Avatar & Header Card */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 overflow-hidden relative">
                            {/* Accent Banner */}
                            <div className="h-32 w-full bg-gradient-to-r from-blue-600 to-indigo-600"></div>
                            
                            <div className="px-8 pb-8">
                                <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 -mt-12 sm:-mt-16 mb-4">
                                    <div className="relative group p-1 bg-white dark:bg-gray-800 rounded-full shadow-md inline-block">
                                        <Avatar 
                                            src={profileData.profile_pic ? (profileData.profile_pic.startsWith('data:') ? profileData.profile_pic : `data:image/png;base64,${profileData.profile_pic}`) : null}
                                            sx={{ width: 120, height: 120, fontSize: '3rem', bgcolor: 'primary.main', border: '2px solid transparent' }}
                                        >
                                            {profileData.name?.charAt(0).toUpperCase() || profileData.email?.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <label className={`absolute bottom-1 right-1 p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg border border-gray-100 dark:border-gray-600 ${isEditingProfile ? 'cursor-pointer hover:scale-105 hover:shadow-xl' : 'cursor-not-allowed opacity-50'} transition-all`}>
                                            <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={!isEditingProfile} />
                                            <Camera className="w-4 h-4 text-primary dark:text-gray-300" />
                                        </label>
                                    </div>
                                    <div className="text-center sm:text-left flex-1 mb-2">
                                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">{profileData.name || 'User Profile'}</h3>
                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-1">{profileData.email}</p>
                                    </div>
                                    <div className="mb-2 w-full sm:w-auto flex justify-center sm:justify-end">
                                        {!isEditingProfile ? (
                                            <Button type="button" onClick={() => setIsEditingProfile(true)} className="px-6 rounded-full font-semibold shadow-sm transition-transform hover:-translate-y-0.5">
                                                Edit Profile
                                            </Button>
                                        ) : (
                                            <div className="flex gap-3">
                                                <Button variant="secondary" type="button" onClick={handleCancelEdit} disabled={loading} className="px-6 rounded-full font-semibold">
                                                    Cancel
                                                </Button>
                                                <Button type="button" onClick={handleProfileUpdate} disabled={loading} className="px-6 rounded-full font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5 border-0">
                                                    {loading ? 'Saving...' : 'Save Changes'}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Details Forms Card */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 p-8 space-y-8 relative">
                            <div>
                                <h4 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white mb-6 pb-3 border-b border-gray-100 dark:border-gray-700/50">
                                    <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    Personal Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                    <div className="md:col-span-2">
                                        <FormInput 
                                            label="Full Name" 
                                            value={profileData.name} 
                                            onChange={e => setProfileData(p => ({ ...p, name: e.target.value }))}
                                            required
                                            readOnly={!isEditingProfile}
                                        />
                                    </div>
                                    <FormInput 
                                        label="Email Address" 
                                        value={profileData.email} 
                                        readOnly 
                                        helperText="Email cannot be changed."
                                        sx={{ bgcolor: 'gray.50' }}
                                    />
                                    <FormInput 
                                        label="Contact Number" 
                                        value={profileData.contact_number} 
                                        onChange={e => setProfileData(p => ({ ...p, contact_number: e.target.value }))}
                                        readOnly={!isEditingProfile}
                                    />
                                </div>
                            </div>

                            <div>
                                <h4 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white mb-6 pb-3 border-b border-gray-100 dark:border-gray-700/50">
                                    <MapPinIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    Organization & Location
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                    <FormInput 
                                        label="Company / Organization" 
                                        value={profileData.company} 
                                        onChange={e => setProfileData(p => ({ ...p, company: e.target.value }))}
                                        readOnly={!isEditingProfile}
                                    />
                                    <FormInput 
                                        label="Location" 
                                        value={profileData.location} 
                                        onChange={e => setProfileData(p => ({ ...p, location: e.target.value }))}
                                        readOnly={!isEditingProfile}
                                    />
                                    <FormInput 
                                        label="State / Province" 
                                        value={profileData.state} 
                                        onChange={e => setProfileData(p => ({ ...p, state: e.target.value }))}
                                        readOnly={!isEditingProfile}
                                    />
                                    <FormInput 
                                        label="Country" 
                                        value={profileData.country} 
                                        onChange={e => setProfileData(p => ({ ...p, country: e.target.value }))}
                                        readOnly={!isEditingProfile}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'password':
                return (
                    <div className="w-full max-w-2xl animate-fade-in">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 overflow-hidden">
                            <div className="p-8 pb-6 border-b border-gray-50 dark:border-gray-700/30 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/20 flex items-center justify-center shrink-0 border border-blue-100/50 dark:border-blue-800/30 shadow-inner">
                                    <Lock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">Security Settings</h3>
                                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-0.5">Manage your account protection</p>
                                </div>
                            </div>
                            
                            <div className="p-8">
                                {/* ── Reset Link Sent: Success Screen ───────────────── */}
                                {isResetMode && resetLinkSent ? (
                                    <div className="text-center py-8 space-y-5">
                                        <div className="mx-auto w-16 h-16 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center border border-green-100 dark:border-green-800/30 shadow-inner">
                                            <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 tracking-tight">Check Your Email</h3>
                                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                We've sent a secure password reset link to{' '}
                                                <strong className="text-gray-700 dark:text-gray-200">{resetData.email}</strong>.
                                            </p>
                                            <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mt-2">
                                                The link will expire in 15 minutes. Check your spam folder if you don't see it.
                                            </p>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                                            <button
                                                type="button"
                                                onClick={() => setResetLinkSent(false)}
                                                className="text-sm font-semibold text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300 bg-blue-50/50 px-6 py-2.5 rounded-lg transition-colors"
                                            >
                                                Resend Link
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => { setIsResetMode(false); setResetLinkSent(false); }}
                                                className="text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-6 py-2.5 rounded-lg transition-colors shadow-sm"
                                            >
                                                Enter Password Manually
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <section className="mb-8 bg-blue-50/60 dark:bg-blue-900/10 p-5 rounded-xl border border-blue-100/80 dark:border-blue-800/50 flex gap-3 shadow-inner">
                                            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                                            <p className="text-sm font-medium text-blue-800 dark:text-blue-300 tracking-wide leading-relaxed">
                                                {isResetMode
                                                    ? "Experiencing login issues? Enter your details below and we'll immediately dispatch a secure reset link."
                                                    : "For your security, configuring a new password will immediately override your active session credentials."}
                                            </p>
                                        </section>

                                        <form onSubmit={isResetMode ? handleResetRequest : handlePasswordChange} className="space-y-6">
                                            {!isResetMode ? (
                                                <div className="space-y-6">
                                                    <PasswordInput
                                                        label="Current Password"
                                                        value={passwordData.current_password}
                                                        onChange={e => setPasswordData(p => ({ ...p, current_password: e.target.value }))}
                                                        required
                                                    />
                                                    <div className="h-px w-full bg-gray-100 dark:bg-gray-700/50 my-2"></div>
                                                    <PasswordInput
                                                        label="New Password"
                                                        value={passwordData.new_password}
                                                        onChange={e => setPasswordData(p => ({ ...p, new_password: e.target.value }))}
                                                        required
                                                    />
                                                    <div className="pl-1">
                                                        <PasswordStrengthIndicator
                                                            password={passwordData.new_password}
                                                            policy={passwordPolicy}
                                                        />
                                                    </div>
                                                    <PasswordInput
                                                        label="Confirm New Password"
                                                        value={passwordData.confirm_password}
                                                        onChange={e => setPasswordData(p => ({ ...p, confirm_password: e.target.value }))}
                                                        required
                                                    />
                                                </div>
                                            ) : (
                                                <div className="space-y-6">
                                                    <FormInput
                                                        label="Verified Email Address"
                                                        type="email"
                                                        value={resetData.email}
                                                        onChange={e => setResetData(p => ({ ...p, email: e.target.value }))}
                                                        required
                                                    />
                                                </div>
                                            )}

                                            <div className="pt-6 mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 dark:border-gray-700/50">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsResetMode(!isResetMode)}
                                                    className="text-sm font-semibold text-gray-500 hover:text-blue-600 transition-colors flex items-center gap-1.5"
                                                >
                                                    {isResetMode ? 'Update via current password' : 'Reset via email instead'}
                                                    <ChevronRight className="w-4 h-4" />
                                                </button>
                                                <Button type="submit" disabled={loading || (!isResetMode && !passwordValidation.isValid)} className="px-6 py-2.5 rounded-lg shadow-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0 hover:shadow-md transition-all hover:-translate-y-0.5 font-semibold">
                                                    {loading ? 'Processing...' : (isResetMode ? 'Dispatch Reset Link' : 'Securely Update')}
                                                </Button>
                                            </div>
                                        </form>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            case 'signout':
                return (
                    <div className="flex flex-col items-center justify-center py-12 space-y-6 animate-fade-in bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700/60 max-w-xl mx-auto mt-8">
                        <div className="p-5 bg-red-50 dark:bg-red-900/20 rounded-full text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900/30">
                            <LogOut className="w-10 h-10" />
                        </div>
                        <div className="text-center px-8">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">End Session</h2>
                            <p className="text-gray-500 dark:text-gray-400 mt-2 font-medium">Are you sure you want to sign out of your current session securely?</p>
                        </div>
                        <div className="flex gap-4 pt-4">
                            <Button variant="secondary" onClick={() => setActiveTab('basic')} className="px-6 rounded-full font-semibold">Keep Working</Button>
                            <Button className="bg-red-600 hover:bg-red-700 text-white px-6 rounded-full font-semibold border-0 hover:shadow-md transition-all hover:-translate-y-0.5" onClick={logout}>Sign Out Now</Button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    // Helper icon since it might not be imported top
    const MapPinIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>;

    return (
        <div className="flex h-full bg-[#f8faff] dark:bg-gray-900 overflow-hidden font-sans">
            {/* Sidebar Navigation */}
            <div className={`transition-all duration-300 bg-white/80 backdrop-blur-xl dark:bg-gray-800/80 border-r border-gray-100 dark:border-gray-700/50 flex flex-col shadow-[4px_0_24px_-12px_rgba(0,0,0,0.1)] z-10 shrink-0 ${isSidebarOpen ? 'w-72' : 'w-20'}`}>
                <div className={`p-6 border-b border-gray-100 dark:border-gray-700/50 flex items-center ${isSidebarOpen ? 'justify-between' : 'justify-center'}`}>
                    <div className={`transition-all duration-300 flex items-center gap-3 ${!isSidebarOpen ? 'w-0 opacity-0 overflow-hidden' : 'w-full opacity-100'}`}>
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-sm">
                            <User className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h3 className="text-[15px] font-bold text-gray-900 dark:text-white leading-tight">
                                User Settings
                            </h3>
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mt-0.5">Preferences</p>
                        </div>
                    </div>
                    <button 
                        onClick={() => setSidebarOpen(!isSidebarOpen)}
                        className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${!isSidebarOpen ? '-mx-2' : ''}`}
                        title={isSidebarOpen ? "Collapse Menu" : "Expand Menu"}
                    >
                        {isSidebarOpen ? <ChevronLeft className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                    </button>
                </div>

                <nav className="flex-1 px-3 py-6 space-y-1.5 overflow-y-auto">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center rounded-xl transition-all duration-200 group relative overflow-hidden ${
                                activeTab === tab.id 
                                    ? 'bg-blue-50/80 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-semibold shadow-sm border border-blue-100/50 dark:border-blue-800/30' 
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 font-medium'
                            } ${!isSidebarOpen ? 'justify-center px-0 py-3.5' : 'justify-start px-4 py-3'}`}
                        >
                            {activeTab === tab.id && isSidebarOpen && (
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-600 to-indigo-600"></div>
                            )}
                            <tab.icon className={`h-5 w-5 shrink-0 transition-colors ${activeTab === tab.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'} ${isSidebarOpen ? 'mr-3.5' : 'mr-0'}`} />
                            {isSidebarOpen && <span className="text-[14px] whitespace-nowrap tracking-wide">{tab.label}</span>}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Main Content Area */}
            <div className="relative flex-1 overflow-y-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden bg-[#f8faff] dark:bg-gray-900">
                {/* Header Background Gradient Sweep (Subtle) */}
                <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-blue-50 via-[#f8faff] to-[#f8faff] dark:from-gray-800/50 dark:via-gray-900 dark:to-gray-900 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100/40 dark:bg-blue-900/10 rounded-full blur-3xl opacity-50"></div>
                    <div className="absolute top-20 -left-20 w-72 h-72 bg-indigo-100/40 dark:bg-indigo-900/10 rounded-full blur-3xl opacity-50"></div>
                </div>
                
                {/* Top-Most Right Close Button */}
                {onBack && (
                     <button 
                        onClick={onBack}
                        className="absolute top-6 right-8 sm:top-8 sm:right-10 z-20 p-2.5 bg-white/80 backdrop-blur dark:bg-gray-800/80 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 transition-all transform hover:rotate-90 shadow-sm border border-gray-100 dark:border-gray-700"
                        title="Close Settings"
                    >
                        <X className="w-5 h-5" />
                    </button>
                )}

                <div className="relative max-w-5xl mx-auto px-6 py-10 sm:px-10 sm:py-12 lg:px-16 lg:py-16 pb-32">
                    <div className="mb-10 animate-fade-in-up">
                        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight flex items-center gap-3">
                            {tabs.find(t => t.id === activeTab)?.label}
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-[15px] mt-2.5 font-medium max-w-2xl leading-relaxed">
                            Manage your personal profile, security settings, and organizational preferences.
                        </p>
                    </div>

                    <div className="animate-fade-in relative z-10">
                        {renderTabContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
